import { useState } from 'react';
import { visibleItems, useInlineEdit, insertLookup, renameLookup, toggleLookupActive } from './utils';

export const FLAT_LIST_CONFIGS = {
  department: {
    // functions has no is_active — edit-only; adding is_active is a one-line migration if needed.
    key: 'departments',
    table: 'functions',
    navLabel: 'Department',
    title: 'Department',
    subtitle: 'Feeds the Department field on the user edit form.',
    addLabel: '+ Add department',
    addPlaceholder: 'Department name',
    hasIsActive: false,
  },
  countries: {
    key: 'countries',
    table: 'resource_countries',
    navLabel: 'Countries',
    title: 'Countries',
    subtitle: 'Country list for resource location.',
    addLabel: '+ Add country',
    addPlaceholder: 'Country name',
    hasIsActive: true,
  },
  skills: {
    key: 'skills',
    table: 'resource_skills',
    navLabel: 'Skills',
    title: 'Skills',
    subtitle: 'Many-to-many with people — feeds the Skills multi-select.',
    addLabel: '+ Add skill',
    addPlaceholder: 'Skill name',
    hasIsActive: true,
  },
  suppliers: {
    key: 'suppliers',
    table: 'resource_suppliers',
    navLabel: 'Suppliers',
    title: 'Suppliers',
    subtitle: 'Also used as the "company" lookup for consultant employer.',
    addLabel: '+ Add supplier',
    addPlaceholder: 'Supplier name',
    hasIsActive: true,
  },
};

export default function FlatListPane({ paneId, items, orgId, setError, reload }) {
  const config = FLAT_LIST_CONFIGS[paneId];
  const [showInactive, setShowInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  const shown = config.hasIsActive ? visibleItems(items, showInactive) : (items || []);

  async function onRename(id, name) {
    setError(null);
    const { error } = await renameLookup(config.table, id, name);
    if (error) setError(error.message);
    else reload();
  }

  async function onToggleActive(id, isCurrentlyActive) {
    setError(null);
    const { error } = await toggleLookupActive(config.table, id, isCurrentlyActive);
    if (error) setError(error.message);
    else reload();
  }

  async function submitAdd() {
    if (!newName.trim()) return;
    setError(null);
    const { error } = await insertLookup(orgId, config.table, newName);
    if (error) setError(error.message);
    else { setNewName(''); setAdding(false); reload(); }
  }

  const edit = useInlineEdit(onRename, setError);

  return (
    <div className="rd-pane">
      <div className="rd-chead">
        <div>
          <h2>{config.title}</h2>
          <p>{config.subtitle}</p>
        </div>
        <div className="rd-chead-actions">
          {config.hasIsActive && (
            <button type="button" className="btn-ghost sm" onClick={() => setShowInactive((v) => !v)}>
              {showInactive ? 'Hide inactive' : 'Show inactive'}
            </button>
          )}
          <button type="button" className="rd-add-btn" onClick={() => setAdding(true)}>{config.addLabel}</button>
        </div>
      </div>
      <div className="rd-list-panel">
        {shown.length === 0 && !adding ? (
          <div className="rd-empty">None yet.</div>
        ) : shown.map((item) => (
          <div key={item.id} className={`rd-lrow${item.is_active === false ? ' muted' : ''}`}>
            {edit.editingId === item.id ? (
              <input
                className="form-input rd-edit-input"
                value={edit.editName}
                onChange={(e) => edit.setEditName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') edit.saveEdit(item.id);
                  if (e.key === 'Escape') edit.cancelEdit();
                }}
                autoFocus
              />
            ) : (
              <span>
                {item.name}
                {config.hasIsActive && item.is_active === false && (
                  <span className="rd-pill inactive inline">Inactive</span>
                )}
              </span>
            )}
            <span className="rd-acts">
              {edit.editingId === item.id ? (
                <>
                  <button type="button" className="rd-link" onClick={() => edit.saveEdit(item.id)}>Save</button>
                  <button type="button" className="rd-link muted" onClick={edit.cancelEdit}>Cancel</button>
                </>
              ) : (
                <>
                  <button type="button" className="rd-link" onClick={() => edit.startEdit(item)}>Edit</button>
                  {config.hasIsActive && (
                    <button type="button" className="rd-link" onClick={() => onToggleActive(item.id, item.is_active !== false)}>
                      {item.is_active === false ? 'Reactivate' : 'Deactivate'}
                    </button>
                  )}
                </>
              )}
            </span>
          </div>
        ))}
        {adding && (
          <div className="rd-add-row">
            <input
              type="text"
              className="form-input"
              placeholder={config.addPlaceholder}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) submitAdd();
                if (e.key === 'Escape') { setAdding(false); setNewName(''); }
              }}
              autoFocus
            />
            <button type="button" className="rd-add-btn" onClick={submitAdd}>Save</button>
            <button type="button" className="btn-ghost sm" onClick={() => { setAdding(false); setNewName(''); }}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}
